import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PendingValidationChips } from "@/components/admin/PendingValidationChips";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { HistoryDrawer } from "@/components/HistoryDrawer";
import {
  LinkChangeBanner,
  LinkChangeByline,
  LinkChangeDecisionControls,
  LinkChangeDiff,
  LinkChangeNote,
  LinkChangeNoteField,
  LinkChangeReviewModal,
} from "@/components/LinkChangeReview";
import { LinkOrderDialog } from "@/components/LinkOrderDialog";
import { SelectionField } from "@/features/expert/components/registration/steps/controls";
import { useCompanySizes, useSectors } from "@/features/questionnaire/api";
import { cn } from "@/lib/utils/cn";

import {
  useActionToolOrder,
  useActionToolOrderDecision,
  useAdminToolsQuery,
  useApproveToolLinkChanges,
  useCreateTool,
  useDeleteTool,
  useImportToolActions,
  useImportTools,
  useToolHistory,
  useToolLinkChange,
  useToolLinkChangeDecision,
  useToolLinkChangesQuery,
  useUpdateTool,
} from "../../api/hooks";
import type { PickerAction, ToolAdmin, ToolType } from "../../types";
import { ActionPicker } from "./ActionPicker";
import { StatusPill } from "./StatusPill";
import { EmptyState, ErrorState, Loading } from "../ui/states";

const TOOL_TYPES: ToolType[] = [
  "guide",
  "action_sheet",
  "video",
  "analysis_grid",
  "other",
];

// The fixed EnterpriseProfile.EnterpriseType keys a tool can target (descriptive
// metadata only — tools are ordered manually, so these don't filter/rank). Labels
// resolve via the shared financialAids.orgTypes.* i18n keys.
const ORG_TYPES = [
  "self_employed",
  "private",
  "cooperative",
  "not_for_profit",
  "economical_development",
  "municipality",
  "other",
] as const;

interface FormState {
  title: string;
  author: string;
  publication_year: string;
  tool_type: ToolType;
  summary: string;
  page_chapter: string;
  resource_url: string;
  is_active: boolean;
  // Descriptive targeting metadata (parity with the Django form): sector ids
  // (M2M) and organization-type keys. Not used to filter/rank the directory.
  sectors_served: string[];
  company_sizes_served: string[];
  organization_types_served: string[];
  // Free-text précisions beside each of the two targeting selectors.
  sectors_served_other: string;
  organization_types_served_other: string;
  actions: PickerAction[];
}

function toFormState(tool?: ToolAdmin): FormState {
  return {
    title: tool?.title ?? "",
    author: tool?.author ?? "",
    publication_year: tool?.publication_year != null ? String(tool.publication_year) : "",
    tool_type: (tool?.tool_type as ToolType) ?? "guide",
    summary: tool?.summary ?? "",
    page_chapter: tool?.page_chapter ?? "",
    resource_url: tool?.resource_url ?? "",
    is_active: tool?.is_active ?? false,
    sectors_served: tool?.sectors_served ?? [],
    company_sizes_served: tool?.company_sizes_served ?? [],
    organization_types_served: tool?.organization_types_served ?? [],
    sectors_served_other: tool?.sectors_served_other ?? "",
    organization_types_served_other: tool?.organization_types_served_other ?? "",
    actions: (tool?.links ?? []).map((l) => ({
      id: l.action,
      name: l.action_name ?? l.action,
      goal: l.goal ?? "",
      issue: l.issue ?? "",
    })),
  };
}

/**
 * The write payload. ``linksFrozen`` omits ``links`` entirely: while a change
 * awaits a second administrator the picker is locked, so a save carries no link
 * edit — and sending the set anyway would have the server report a link change
 * as dropped when the administrator only corrected a title.
 */
function toBody(form: FormState, linksFrozen = false): Partial<ToolAdmin> {
  return {
    title: form.title,
    author: form.author,
    publication_year: form.publication_year ? Number(form.publication_year) : undefined,
    tool_type: form.tool_type,
    summary: form.summary,
    page_chapter: form.page_chapter,
    resource_url: form.resource_url,
    is_active: form.is_active,
    sectors_served: form.sectors_served,
    company_sizes_served: form.company_sizes_served,
    organization_types_served:
      form.organization_types_served as ToolAdmin["organization_types_served"],
    sectors_served_other: form.sectors_served_other,
    organization_types_served_other: form.organization_types_served_other,
    // No per-tool order: the picker no longer ranks a tool's actions — ordering is
    // per action, staged behind four-eyes (see the reorder flow).
    ...(linksFrozen
      ? {}
      : { links: form.actions.map((item) => ({ action: item.id })) }),
  } as Partial<ToolAdmin>;
}

/** CQDD tools back-office: a list with create/edit form and .xlsx import. */
export function AdminApp() {
  const { t } = useTranslation();
  const list = useAdminToolsQuery();
  const createTool = useCreateTool();
  const updateTool = useUpdateTool();
  const deleteTool = useDeleteTool();
  const importTools = useImportTools();
  const importToolActions = useImportToolActions();
  // Reference list for the sectors selector (shared with the directory).
  const sectors = useSectors();
  const companySizes = useCompanySizes();

  // Held by id and re-read from the list query, never snapshotted: a
  // decision taken in the reorder dialog invalidates that query, and a
  // copy taken at click time would keep reporting the state the row had
  // before it (the "à valider" chip outliving its own approval).
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing =
    (editingId && (list.data ?? []).find((item) => item.id === editingId)) ||
    null;
  // The reorder dialog is opened per linked action, from the list below the
  // picker.
  const [orderAction, setOrderAction] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(toFormState());
  // Per-item change-history drawer (titled with the tool's name, like the
  // Django drawer), opened from the edit form — not the listing.
  const [historyItem, setHistoryItem] = useState<{ id: string; name: string } | null>(
    null,
  );
  const history = useToolHistory(historyItem?.id ?? null);

  // Link changes staged for *this* administrator to validate: the list banner
  // and its review modal, and — on the edit form — the decision on one tool.
  const linkChanges = useToolLinkChangesQuery();
  const approveLinkChanges = useApproveToolLinkChanges();
  const [reviewing, setReviewing] = useState(false);
  // "En attente de validation" only, the same switch the Django list carries.
  const [pendingOnly, setPendingOnly] = useState(false);
  const pendingChange = useToolLinkChange(editingId);
  const decideLinkChange = useToolLinkChangeDecision(editingId ?? "");
  const pending = pendingChange.data ?? null;
  const [countering, setCountering] = useState(false);
  const [counterNote, setCounterNote] = useState("");
  const [counterActions, setCounterActions] = useState<PickerAction[]>([]);
  // The proposed set, shown instead of the live one while a change is staged —
  // a reviewer decides on what was proposed, not on what is live.
  const proposedActions: PickerAction[] = (pending?.proposed ?? []).map(
    (action) => ({
      id: action.id,
      name: action.name,
      goal: action.goal,
      issue: action.issue,
    }),
  );
  // Which of them approval would *add* — marked in the locked list, as the
  // Django proposed list marks them.
  const proposedNewIds = new Set(
    (pending?.proposed ?? []).filter((a) => a.is_new).map((a) => a.id),
  );

  // Only a *saved* link can be reordered, and only where there is something to
  // order — the count and the pending state ride on the link, so the row's
  // button costs no extra request. A row the administrator just added ranks
  // nothing yet and gets no button.
  const orderByAction = new Map(
    (editing?.links ?? [])
      .filter(
        // The reorder endpoint 404s once the action's thematic stops offering
        // this kind of resource, and the links outlive that flag — so no button.
        (link) =>
          (link.action_order_allowed ?? false) &&
          ((link.action_link_count ?? 0) > 1 ||
            (link.action_order_pending ?? false)),
      )
      .map((link) => [
        link.action,
        {
          count: link.action_link_count ?? 0,
          // Two very different states: "à valider" is waiting on you, "en
          // attente" is your own proposal waiting on someone else.
          pendingReview:
            (link.action_order_pending ?? false) &&
            !(link.action_order_pending_mine ?? false),
          pendingMine: link.action_order_pending_mine ?? false,
        },
      ]),
  );

  // Sectors grouped by category for the searchable switch list (parity with the
  // expert registration selector; uncategorized sectors last under "Autres").
  const uncategorized = t("expert.register.uncategorized");
  const sectorGroups = (() => {
    const map = new Map<string, { value: string; label: string }[]>();
    for (const s of sectors.data ?? []) {
      const heading = s.category?.name ?? uncategorized;
      const list = map.get(heading) ?? [];
      list.push({ value: s.id, label: s.name });
      map.set(heading, list);
    }
    return [...map.entries()]
      .sort(([a], [b]) =>
        a === uncategorized ? 1 : b === uncategorized ? -1 : a.localeCompare(b),
      )
      .map(([heading, options]) => ({ heading, options }));
  })();

  const startCreate = () => {
    setForm(toFormState());
    setEditingId(null);
    setCreating(true);
  };
  const startEdit = (tool: ToolAdmin) => {
    setForm(toFormState(tool));
    setEditingId(tool.id);
    setCreating(true);
  };
  const cancel = () => {
    setCreating(false);
    setEditingId(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const saved = editing
        ? await updateTool.mutateAsync({
            id: editing.id,
            body: toBody(form, Boolean(pending)),
          })
        : await createTool.mutateAsync(toBody(form));
      toast.success(t(editing ? "tools.admin.updated" : "tools.admin.created"));
      reportLinkOutcome(saved);
      cancel();
    } catch {
      toast.error(t("tools.error"));
    }
  };

  const remove = async (tool: ToolAdmin) => {
    if (!window.confirm(t("tools.admin.confirmDelete"))) return;
    try {
      await deleteTool.mutateAsync(tool.id);
      toast.success(t("tools.admin.deleted"));
    } catch {
      toast.error(t("tools.error"));
    }
  };

  // A save never applies the links: it stages them, and while a change is
  // already pending it stages nothing at all. Either way the administrator has
  // to be told — a silent success would read as "the links are live now".
  const reportLinkOutcome = (saved: unknown) => {
    const outcome = (saved as { link_change_outcome?: string | null } | null)
      ?.link_change_outcome;
    if (outcome === "staged") toast.info(t("tools.linkChange.staged"));
    if (outcome === "frozen") toast.warning(t("tools.linkChange.frozen"));
  };

  const startCounter = () => {
    // A counter-proposal starts from what was proposed, not from the live set.
    setCounterActions(proposedActions);
    setCounterNote("");
    setCountering(true);
  };

  const decide = async (decision: Parameters<typeof decideLinkChange.mutateAsync>[0]) => {
    try {
      await decideLinkChange.mutateAsync(decision);
      setCountering(false);
      setCounterNote("");
      toast.success(
        t(
          decision.decision === "approve"
            ? "tools.linkChange.approved"
            : "tools.linkChange.countered",
        ),
      );
    } catch (e) {
      // ``unwrap`` throws the normalized ApiError shape — the service's message
      // (already localized) lands in ``detail``, and ``code`` says how to read
      // it. ``noop`` means the reviewer asked for nothing, which is information
      // rather than a failure: the same split ``_report`` makes in Django.
      const error = e as { detail?: string; code?: string } | null;
      const report = error?.code === "noop" ? toast.info : toast.error;
      report(error?.detail || t("tools.error"));
    }
  };

  const approveAll = async (requestIds: string[]) => {
    try {
      const approved = (await approveLinkChanges.mutateAsync(requestIds) as {
        approved: number;
      }).approved;
      setReviewing(false);
      // Nothing approved is not a success: every row may have been skipped as
      // the reader's own proposal, or resolved by someone else meanwhile.
      // Django says "Aucun changement à approuver." here; so do we.
      if (approved === 0) {
        toast.info(t("tools.linkChange.noneToApprove"));
      } else {
        toast.success(t("tools.linkChange.approvedCount", { count: approved }));
      }
    } catch {
      toast.error(t("tools.error"));
    }
  };

  const onImport = async (file: File) => {
    try {
      const report = await importTools.mutateAsync(file);
      toast.success(
        t("tools.admin.imported", {
          created: report.created,
          updated: report.updated,
          skipped: report.skipped,
        }),
      );
      reportCancelledReviews(report);
    } catch {
      toast.error(t("tools.error"));
    }
  };

  const onImportActions = async (file: File) => {
    try {
      const report = await importToolActions.mutateAsync(file);
      toast.success(
        t("tools.admin.actionsImported", {
          pairs: report.pairs_applied,
          tools: report.tools_matched,
        }),
      );
      reportCancelledReviews(report);
    } catch {
      toast.error(t("tools.error"));
    }
  };

  // The import writes link sets directly and so ends any review open on the
  // resources it touches. Say which — ending somebody else's review should not
  // be something an administrator only finds later in a history drawer.
  const reportCancelledReviews = (report: { reviews_cancelled?: string[] }) => {
    const titles = report.reviews_cancelled ?? [];
    if (titles.length > 0) {
      toast.warning(
        t("tools.admin.importCancelledReviews", {
          count: titles.length,
          titles: titles.join(", "),
        }),
      );
    }
  };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // The pending-validation filter. `awaiting_validation_pending` is the server's
  // answer — the very set Django's own switch filters on — rather than
  // something re-derived from the links here: a staged order is keyed on an
  // *action*, so `action_order_pending` is true for every resource linked to
  // it, moved or not, and the list would read wider than the back-office's.
  const visible = (list.data ?? []).filter(
    (tool) => !pendingOnly || tool.awaiting_validation_pending,
  );

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">{t("tools.admin.title")}</h1>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer">
            <span className="inline-flex h-9 items-center rounded-md border border-border px-3 text-sm font-semibold hover:bg-accent">
              {t("tools.admin.import")}
            </span>
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onImport(file);
                e.target.value = "";
              }}
            />
          </label>
          <label className="cursor-pointer">
            <span className="inline-flex h-9 items-center rounded-md border border-border px-3 text-sm font-semibold hover:bg-accent">
              {t("tools.admin.importActions")}
            </span>
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onImportActions(file);
                e.target.value = "";
              }}
            />
          </label>
          <Button onClick={startCreate}>{t("tools.admin.create")}</Button>
        </div>
      </header>

      {creating && (
        <Card>
          <CardContent className="p-4">
            <form onSubmit={submit} className="space-y-3">
              {/* Active is a record-level property — a header switch (Actif /
                  Inactif), mirroring the Django tool form. */}
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-foreground">
                  {editing ? t("tools.admin.editTool") : t("tools.admin.create")}
                </h2>
                <div className="ml-auto flex items-center gap-3">
                {editing && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setHistoryItem({ id: editing.id, name: editing.title })
                    }
                  >
                    {t("tools.admin.history")}
                  </Button>
                )}
                <label className="flex cursor-pointer items-center gap-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(next) => set("is_active", next)}
                    label={t("tools.admin.fields.isActive")}
                  />
                  <span
                    className={cn(
                      "min-w-[3.5rem] text-sm font-semibold",
                      form.is_active ? "text-success" : "text-muted-foreground",
                    )}
                  >
                    {form.is_active
                      ? t("tools.admin.fields.isActive")
                      : t("tools.admin.fields.isInactive")}
                  </span>
                </label>
                </div>
              </div>
              <Field label={t("tools.admin.fields.title")}>
                <input
                  className="w-full rounded-md border border-border px-3 py-2"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  required
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("tools.admin.fields.author")}>
                  <input
                    className="w-full rounded-md border border-border px-3 py-2"
                    value={form.author}
                    onChange={(e) => set("author", e.target.value)}
                  />
                </Field>
                <Field label={t("tools.admin.fields.publicationYear")}>
                  <input
                    type="number"
                    min={1900}
                    max={new Date().getFullYear() + 1}
                    step={1}
                    className="w-full rounded-md border border-border px-3 py-2"
                    value={form.publication_year}
                    onChange={(e) => set("publication_year", e.target.value)}
                    required
                  />
                </Field>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("tools.admin.fields.type")}>
                  <select
                    className="w-full rounded-md border border-border px-3 py-2"
                    value={form.tool_type}
                    onChange={(e) => set("tool_type", e.target.value as ToolType)}
                  >
                    {TOOL_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {t(`tools.types.${type}`)}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t("tools.admin.fields.resourceUrl")}>
                  <input
                    type="url"
                    className="w-full rounded-md border border-border px-3 py-2"
                    value={form.resource_url}
                    onChange={(e) => set("resource_url", e.target.value)}
                    required
                  />
                </Field>
              </div>
              <Field label={t("tools.admin.fields.summary")}>
                <textarea
                  className="w-full rounded-md border border-border px-3 py-2"
                  rows={3}
                  maxLength={700}
                  value={form.summary}
                  onChange={(e) => set("summary", e.target.value)}
                />
              </Field>
              <Field label={t("tools.admin.fields.pageChapter")}>
                <input
                  type="text"
                  className="w-full rounded-md border border-border px-3 py-2"
                  value={form.page_chapter}
                  onChange={(e) => set("page_chapter", e.target.value)}
                />
              </Field>
              <SelectionField
                label={t("tools.admin.fields.sectorsServed")}
                options={(sectors.data ?? []).map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                groups={sectorGroups}
                selected={form.sectors_served}
                onChange={(next) => set("sectors_served", next)}
                selectAllLabel={t("expert.register.selectAllSectors")}
                otherToggleLabel={t("tools.admin.otherToggle")}
                otherLabel={t("tools.admin.fields.sectorsServedOther")}
                otherValue={form.sectors_served_other}
                onOtherChange={(next) => set("sectors_served_other", next)}
              />
              <SelectionField
                label={t("tools.admin.fields.companySizes")}
                options={(companySizes.data ?? []).map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                selected={form.company_sizes_served}
                onChange={(next) => set("company_sizes_served", next)}
                selectAllLabel={t("expert.register.selectAllSizes")}
              />
              <SelectionField
                label={t("tools.admin.fields.organizationTypes")}
                options={ORG_TYPES.map((key) => ({
                  value: key,
                  label: t(`financialAids.orgTypes.${key}`),
                }))}
                selected={form.organization_types_served}
                onChange={(next) => set("organization_types_served", next)}
                selectAllLabel={t("expert.register.selectAllTypes")}
                otherToggleLabel={t("tools.admin.otherToggle")}
                otherLabel={t("tools.admin.fields.organizationTypesOther")}
                otherValue={form.organization_types_served_other}
                onOtherChange={(next) =>
                  set("organization_types_served_other", next)
                }
              />
              <div className="space-y-1 text-sm">
                <span className="font-medium">{t("tools.admin.fields.actions")}</span>
                {pending && (
                  <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3">
                    <p className="text-sm text-foreground">
                      {t(
                        pending.is_mine
                          ? "tools.linkChange.formPendingMine"
                          : "tools.linkChange.formPendingReview",
                      )}
                    </p>
                    <LinkChangeByline pending={pending} ns="tools" />
                    <LinkChangeDiff pending={pending} ns="tools" />
                    <LinkChangeNote note={pending.note} ns="tools" />
                  </div>
                )}
                <ActionPicker
                  // While a change is staged the picker shows the *proposed*
                  // set, locked — except to a reviewer drafting a counter.
                  value={
                    countering
                      ? counterActions
                      : pending
                        ? proposedActions
                        : form.actions
                  }
                  locked={Boolean(pending) && !countering}
                  newIds={countering ? undefined : proposedNewIds}
                  onChange={(actions) =>
                    countering ? setCounterActions(actions) : set("actions", actions)
                  }
                  rowAction={(action) => {
                    const order = orderByAction.get(action.id);
                    if (!order) return null;
                    return (
                      <span className="flex items-center gap-1.5">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          // Outlined in ink, not the pale default: it sits next
                          // to a muted chip and a quiet remove icon and has to
                          // read as the row's real action (matches Django).
                          className="border-foreground text-foreground"
                          onClick={() =>
                            setOrderAction({ id: action.id, name: action.name })
                          }
                        >
                          {t("tools.linkOrder.open", { count: order.count })}
                        </Button>
                        {(order.pendingReview || order.pendingMine) && (
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                              order.pendingReview
                                ? "bg-warning/20 text-foreground"
                                : "bg-muted text-muted-foreground",
                            )}
                          >
                            {t(
                              order.pendingReview
                                ? "tools.linkOrder.toValidate"
                                : "tools.linkOrder.pending",
                            )}
                          </span>
                        )}
                      </span>
                    );
                  }}
                />
              </div>
              {countering && (
                <LinkChangeNoteField
                  ns="tools"
                  note={counterNote}
                  onNote={setCounterNote}
                />
              )}
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={cancel}>
                  {t("tools.admin.cancel")}
                </Button>
                {pending && (
                  <LinkChangeDecisionControls
                    pending={pending}
                    ns="tools"
                    countering={countering}
                    note={counterNote}
                    onStartCounter={startCounter}
                    onCancelCounter={() => setCountering(false)}
                    busy={decideLinkChange.isPending}
                    onApprove={() =>
                      void decide({
                        decision: "approve",
                        request_id: pending.id,
                      })
                    }
                    onCounter={() =>
                      void decide({
                        decision: "counter",
                        request_id: pending.id,
                        ids: counterActions.map((a) => a.id),
                        note: counterNote,
                      })
                    }
                  />
                )}
                {/* Saving stays available while a change is staged: only the
                    links are frozen, the resource's own fields are not. */}
                {!countering && (
                  <Button type="submit">
                    {t(pending ? "tools.linkChange.saveOthers" : "tools.admin.save")}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <LinkChangeBanner
        count={(linkChanges.data ?? []).length}
        ns="tools"
        onOpen={() => setReviewing(true)}
      />

      {/* How many tools the list holds, as on the Django list. The response is
          unpaginated here, so its length *is* the total. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {list.data && list.data.length > 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("tools.admin.countTotal", { count: visible.length })}
          </p>
        ) : (
          <span />
        )}
        {/* The Django list's "En attente de validation" switch. A staged
            *display order* counts too — either way the row awaits somebody. */}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Switch
            checked={pendingOnly}
            onCheckedChange={setPendingOnly}
            label={t("tools.linkChange.pendingOnly")}
          />
          <span className="font-medium">{t("tools.linkChange.pendingOnly")}</span>
        </label>
      </div>

      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorState onRetry={() => void list.refetch()} />
      ) : visible.length === 0 ? (
        <EmptyState
          title={t(pendingOnly ? "tools.linkChange.noneAwaiting" : "tools.admin.empty")}
        />
      ) : (
        <div className="space-y-2">
          {visible.map((tool) => (
            <Card key={tool.id}>
              <CardContent className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-semibold">{tool.title}</p>
                    <StatusPill status={tool.status} />
                    {/* What this row awaits validation for — the same chips the
                        Django list shows beside the title. */}
                    <PendingValidationChips
                      namespace="tools"
                      status={tool.status}
                      linkChangePending={tool.link_change_pending}
                      linkChangePendingMine={tool.link_change_pending_mine}
                      orderChangePending={tool.order_change_pending}
                      orderChangePendingMine={tool.order_change_pending_mine}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {tool.tool_type_display} · {tool.author} ·{" "}
                    {t("tools.admin.actionCount", {
                      count: tool.action_link_count,
                    })}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startEdit(tool)}
                  >
                    {t("tools.admin.edit")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void remove(tool)}
                  >
                    {t("tools.admin.delete")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {historyItem && (
        <HistoryDrawer
          title={historyItem.name}
          onClose={() => setHistoryItem(null)}
          query={history}
        />
      )}
      {reviewing && (
        <LinkChangeReviewModal
          rows={linkChanges.data ?? []}
          ns="tools"
          isPending={approveLinkChanges.isPending}
          onApproveAll={(ids) => void approveAll(ids)}
          onOpenResource={(id) => {
            const tool = (list.data ?? []).find((item) => item.id === id);
            if (tool) startEdit(tool);
            setReviewing(false);
          }}
          onClose={() => setReviewing(false)}
        />
      )}
      {orderAction && (
        <LinkOrderDialog
          action={orderAction}
          ns="tools"
          useOrder={useActionToolOrder}
          useDecision={useActionToolOrderDecision}
          onClose={() => setOrderAction(null)}
        />
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
